/**
 * useOnboardingWelcome — auto-opens chat on the user's first visit
 * to /desktop after completing onboarding. The personalized welcome
 * message is delivered via the static `chatWelcomeMessage` stored in
 * accounts/{uid}/config/runtime (written during Onboarding.tsx save).
 *
 * This hook only handles:
 *   1. Auto-opening the chat panel on first desktop visit
 *   2. Marking needsWelcome = false so it doesn't re-trigger
 *
 * Prest0n's persistent memory of the user comes from accounts/{uid}/memory/onboarding_profile
 * (also written during Onboarding.tsx save), which the VM reads on every session.
 */
import { useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, updateDoc, getFirestore } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';

interface WelcomeActions {
  openChat: () => void;
  sendMessage: (text: string) => void;
}

export function useOnboardingWelcome(
  actions: WelcomeActions | null,
  isReady: boolean,
  _sessionReady: boolean,  // kept for API compatibility, not used
) {
  const firedRef = useRef(false);
  const { pathname } = useLocation();

  useEffect(() => {
    // Only trigger on workspace pages
    if (!isReady || !actions || firedRef.current) return;
    if (pathname !== '/desktop' && pathname !== '/agent-activity') return;

    const checkAndOpen = async () => {
      try {
        const user = getAuth().currentUser;
        if (!user) return;

        const db = getFirestore();
        const ref = doc(db, 'accounts', user.uid, 'onboarding', 'profile');
        const snap = await getDoc(ref);
        if (!snap.exists()) return;

        const profile = snap.data();
        if (!profile?.needsWelcome) return;

        firedRef.current = true;

        // Mark consumed immediately to prevent double-fire
        await updateDoc(ref, { needsWelcome: false });

        // Auto-open the chat so the welcome message is immediately visible
        actions.openChat();

      } catch (err) {
        console.warn('[OnboardingWelcome] Failed to check profile:', err);
      }
    };

    checkAndOpen();
  }, [isReady, actions, pathname]);
}
